import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, FileText, ClipboardCheck, Upload } from 'lucide-react';
import { lmsApi, submissionApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function LessonView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [lesson, setLesson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('transcript');
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [isSavingTranscript, setIsSavingTranscript] = useState(false);
  const progressInterval = useRef(null);
  const isStudent = user?.role === 'student';

  // Submission state
  const [submissionTitle, setSubmissionTitle] = useState('');
  const [submissionDesc, setSubmissionDesc] = useState('');
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadLesson();
    return () => {
      if (progressInterval.current) clearInterval(progressInterval.current);
    };
  }, [id]);

  const loadLesson = async () => {
    try {
      const res = await lmsApi.getLesson(id);
      setLesson(res.data);
      setTranscriptText(res.data.transcript || '');

      // Start progress tracking for students
      if (user?.role === 'student' && res.data.youtube_video_id) {
        startProgressTracking();
      }
    } catch (error) {
      toast.error('Failed to load lesson');
      navigate('/lms/courses');
    } finally {
      setIsLoading(false);
    }
  };

  const startProgressTracking = () => {
    let seconds = 0;
    progressInterval.current = setInterval(async () => {
      seconds += 30;
      try {
        await lmsApi.updateProgress(id, { progressSeconds: seconds, completed: false });
      } catch (e) { /* ignore */ }
    }, 30000);
  };

  const handleMarkComplete = async () => {
    try {
      await lmsApi.updateProgress(id, { progressSeconds: lesson.duration_seconds || 0, completed: true });
      toast.success('Lesson marked as completed!');
      loadLesson();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handleSaveTranscript = async () => {
    setIsSavingTranscript(true);
    try {
      await lmsApi.updateTranscript(id, transcriptText);
      toast.success('Transcript saved');
      setIsEditingTranscript(false);
      loadLesson();
    } catch (error) {
      toast.error('Failed to save transcript');
    } finally {
      setIsSavingTranscript(false);
    }
  };

  const handleSubmitWork = async (e) => {
    e.preventDefault();
    if (!submissionTitle.trim()) {
      toast.error('Title is required');
      return;
    }
    setIsSubmitting(true);
    try {
      await submissionApi.create({
        title: submissionTitle,
        description: submissionDesc,
        lessonId: id,
        files: submissionFiles
      });
      toast.success('Work submitted!');
      setSubmissionTitle('');
      setSubmissionDesc('');
      setSubmissionFiles([]);
    } catch (error) {
      toast.error('Failed to submit work');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!lesson) return null;

  const tabs = [
    { id: 'transcript', label: 'Transcript', icon: FileText },
    ...(lesson.assessment ? [{ id: 'assessment', label: 'Assessment', icon: ClipboardCheck }] : []),
    ...(isStudent ? [{ id: 'submit', label: 'Submit Work', icon: Upload }] : [])
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(`/lms/courses/${lesson.course_id}`)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {lesson.course_title || 'Back to Course'}
        </button>

        <div className="flex items-center space-x-2">
          {lesson.prevLesson && (
            <button
              onClick={() => navigate(`/lms/lessons/${lesson.prevLesson.id}`)}
              className="flex items-center px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Prev
            </button>
          )}
          {lesson.nextLesson && (
            <button
              onClick={() => navigate(`/lms/lessons/${lesson.nextLesson.id}`)}
              className="flex items-center px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </button>
          )}
        </div>
      </div>

      {/* Title */}
      <h1 className="text-xl font-bold text-gray-900 mb-4">{lesson.title}</h1>

      {/* Content */}
      <>
          {/* Video Player */}
          {lesson.youtube_video_id ? (
            <div className="mb-6">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://www.youtube.com/embed/${lesson.youtube_video_id}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full rounded-lg"
                />
              </div>

              {isStudent && lesson.progress?.status !== 'completed' && (
                <button
                  onClick={handleMarkComplete}
                  className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                >
                  Mark as Completed
                </button>
              )}

              {isStudent && lesson.progress?.status === 'completed' && (
                <p className="mt-3 text-green-600 text-sm font-medium">Lesson completed</p>
              )}
            </div>
          ) : (
            <div className="mb-6 bg-gray-100 rounded-lg p-8 text-center text-gray-500">
              No video available for this lesson
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-4">
            <div className="flex space-x-6">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center pb-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-1.5" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'transcript' && (
            <div>
              {!isStudent && (
                <div className="flex items-center space-x-2 mb-3">
                  <button
                    onClick={() => setIsEditingTranscript(!isEditingTranscript)}
                    className="text-sm px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    {isEditingTranscript ? 'Cancel' : 'Edit Transcript'}
                  </button>
                  {isEditingTranscript && (
                    <button
                      onClick={handleSaveTranscript}
                      disabled={isSavingTranscript}
                      className="text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      {isSavingTranscript ? 'Saving...' : 'Save'}
                    </button>
                  )}
                </div>
              )}

              {isEditingTranscript ? (
                <textarea
                  value={transcriptText}
                  onChange={(e) => setTranscriptText(e.target.value)}
                  rows={15}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  placeholder="Enter transcript text..."
                />
              ) : lesson.transcript ? (
                <div className="bg-white border border-gray-200 rounded-lg p-6 whitespace-pre-wrap text-sm text-gray-700 leading-relaxed max-h-96 overflow-y-auto">
                  {lesson.transcript}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No transcript available</p>
              )}
            </div>
          )}

          {activeTab === 'assessment' && lesson.assessment && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900">{lesson.assessment.title}</h3>
              <p className="text-sm text-gray-500 mt-1 capitalize">{lesson.assessment.assessment_type}</p>
              <button
                onClick={() => navigate(`/lms/assessments/${lesson.assessment.id}`)}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Start Assessment
              </button>
            </div>
          )}

          {activeTab === 'submit' && isStudent && (
            <form onSubmit={handleSubmitWork} className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Submit Your Work</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={submissionTitle}
                  onChange={(e) => setSubmissionTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Submission title"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={submissionDesc}
                  onChange={(e) => setSubmissionDesc(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Describe your work..."
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Files (PDF, images, audio)</label>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,audio/*"
                  onChange={(e) => setSubmissionFiles(Array.from(e.target.files))}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                {submissionFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{submissionFiles.length} file(s) selected</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Work'}
              </button>
            </form>
          )}
        </>
    </div>
  );
}

export default LessonView;
